import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { toast } from "sonner";
import MembershipCard from "@/components/MembershipCard";
import MaskProgress from "@/components/MaskProgress";
import { LogOut, Settings, Gift, Calendar, Crown, Star, Share2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_obsidian-tier/artifacts/za07r3ra_dungeon-eyes-wide-shut-logo-bigger-text.png";
const BG_IMAGE = "https://customer-assets.emergentagent.com/job_obsidian-tier/artifacts/ku4hg5uq_sex%20toy%20shop.png";

export default function DashboardPage() {
  const { member, logout, refreshMember } = useAuth();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [cardFlipped, setCardFlipped] = useState(false);

  useEffect(() => {
    loadVisits();
  }, []);

  const loadVisits = async () => {
    try {
      const res = await api.get("/member/visits");
      setVisits(res.data);
    } catch (error) {
      console.error("Failed to load visits", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Until we meet again in the shadows");
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Dungeon-Eyes Wide Shut Membership',
      text: `Join me in the shadows! I'm a ${member.tier_name} member at Dungeon-Eyes Wide Shut.`,
      url: 'https://dungeon-eyeswideshut.co.uk'
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success("Shared successfully");
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        toast.success("Link copied to clipboard");
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error("Could not share");
      }
    }
  };

  const freeEveningsAvailable = member.free_evenings_earned - member.free_evenings_redeemed;

  return (
    <div className="min-h-screen bg-[#050505] relative">
      {/* Background Image */}
      <div 
        className="fixed inset-0 opacity-40 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/50 to-[#050505]/70" />
      
      {/* Header */}
      <header className="relative z-10 border-b border-[#c5a059]/10 bg-[#050505]/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <img 
            src={LOGO_URL} 
            alt="Logo" 
            className="h-12 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate("/dashboard")}
            data-testid="dashboard-logo"
          />
          <div className="flex items-center gap-4">
            {member.is_admin && (
              <button
                onClick={() => navigate("/admin")}
                className="flex items-center gap-2 px-4 py-2 text-[#c5a059] hover:text-[#e5c07b] 
                  border border-[#c5a059]/30 rounded hover:border-[#c5a059]/60 transition-all text-sm"
                data-testid="admin-link"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Admin</span>
              </button>
            )}
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 text-[#c5a059] hover:text-[#e5c07b] 
                border border-[#c5a059]/30 rounded hover:border-[#c5a059]/60 transition-all text-sm"
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-[#c5a059] hover:text-[#e5c07b] 
                border border-[#c5a059]/30 rounded hover:border-[#c5a059]/60 transition-all text-sm"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Exit</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* Welcome */}
        <div className="text-center mb-8 animate-fade-in-up">
          <h1 className="font-cinzel text-2xl md:text-3xl tracking-[0.15em] uppercase mb-2" style={{ color: '#c5a059' }}>
            Welcome, {member.display_name}
          </h1>
          <p className="text-[#c5a059]/50 text-sm tracking-wider">
            Member since {new Date(member.member_since).toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}
          </p>
        </div>

        {/* Main Grid - Card on left, Stats + Tiers on right */}
        <div className="flex flex-col xl:flex-row gap-8 items-start justify-center">
          {/* Left Column - Membership Card */}
          <div className="animate-fade-in-up stagger-1 flex-shrink-0">
            <MembershipCard 
              member={member} 
              flipped={cardFlipped} 
              onFlip={() => setCardFlipped(!cardFlipped)}
            />
            <p className="text-center mt-4 text-[#c5a059]/40 text-xs tracking-wider">
              Tap card to reveal your QR code
            </p>
          </div>

          {/* Right Column - Stats, Progress, Privileges, Tiers */}
          <div className="flex-1 max-w-2xl space-y-6 animate-fade-in-up stagger-2">
            {/* Top Row: Progress + Stats */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Current Progress */}
              <div className="obsidian-card rounded-xl p-5">
                <h3 className="font-cinzel text-xs tracking-[0.2em] uppercase mb-3" style={{ color: '#c5a059' }}>
                  Progress to Next Reward
                </h3>
                <MaskProgress filled={member.current_cycle_visits} total={5} />
                <p className="text-center mt-3 text-[#e5c07b] text-sm">
                  <span className="font-semibold">{member.visits_to_next_reward}</span> visit{member.visits_to_next_reward !== 1 ? 's' : ''} to free evening
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="stat-card" data-testid="total-visits-stat">
                  <div className="stat-value">{member.total_visits}</div>
                  <div className="stat-label">Total Visits</div>
                </div>
                <div className={`stat-card ${freeEveningsAvailable > 0 ? 'reward-available' : ''}`} data-testid="rewards-stat">
                  <div className="stat-value" style={{ color: freeEveningsAvailable > 0 ? '#b30000' : undefined }}>
                    {freeEveningsAvailable}
                  </div>
                  <div className="stat-label">Free Evenings</div>
                </div>
              </div>
            </div>

            {/* Second Row: Privileges */}
            <div className="obsidian-card rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-4 h-4 text-[#c5a059]" />
                <h3 className="font-cinzel text-xs tracking-[0.15em] uppercase" style={{ color: '#c5a059' }}>
                  Your Privileges
                </h3>
              </div>
              <ul className="grid grid-cols-2 gap-2">
                {member.tier_perks.filter(perk => !perk.toLowerCase().includes('champagne')).map((perk, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#e5c07b]/80 text-sm">
                    <Star className="w-3 h-3 text-[#c5a059] flex-shrink-0 mt-1" />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              {/* Champagne privilege - centered on its own line */}
              {member.tier_perks.some(perk => perk.toLowerCase().includes('champagne')) && (
                <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-[#c5a059]/20">
                  <Star className="w-3 h-3 text-[#c5a059]" />
                  <span className="text-[#e5c07b]/80 text-sm">Free bottle of champagne on overnight stays</span>
                  <Star className="w-3 h-3 text-[#c5a059]" />
                </div>
              )}
            </div>

            {/* Third Row: Tier Progression */}
            <div>
              <h3 className="font-cinzel text-xs tracking-[0.2em] uppercase mb-3" style={{ color: '#c5a059' }}>
                Tier Progression
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <TierCard 
                  name="Shadow Initiate"
                  range="0-9 visits"
                  description="Entry into the shadows"
                  active={member.tier === 'shadow_initiate'}
                  tier="shadow"
                />
                <TierCard 
                  name="Crimson Devotee"
                  range="10-24 visits"
                  description="Priority booking, Event invites"
                  active={member.tier === 'crimson_devotee'}
                  tier="crimson"
                />
                <TierCard 
                  name="Obsidian Master"
                  range="25+ visits"
                  description="VIP upgrades, Private chambers"
                  active={member.tier === 'obsidian_master'}
                  tier="obsidian"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Recent Visits */}
        {visits.length > 0 && (
          <div className="mt-12 animate-fade-in-up stagger-4 max-w-4xl mx-auto">
            <h2 className="font-cinzel text-lg tracking-[0.15em] uppercase text-center mb-4" style={{ color: '#c5a059' }}>
              Recent Visits
            </h2>
            <div className="obsidian-card rounded-xl overflow-hidden">
              <div className="max-h-48 overflow-y-auto">
                {visits.slice(0, 10).map((visit) => (
                  <div 
                    key={visit.id} 
                    className="flex items-center justify-between p-3 border-b border-[#c5a059]/10 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-[#c5a059]/50" />
                      <span className="text-[#e5c07b] text-sm">
                        {new Date(visit.visit_date).toLocaleDateString('en-GB', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full ${
                      visit.session_type === 'evening' 
                        ? 'bg-[#8a0303]/20 text-[#ff6b6b]' 
                        : 'bg-[#c5a059]/10 text-[#c5a059]'
                    }`}>
                      {visit.session_type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#c5a059]/10 mt-12 py-8">
        <div className="text-center">
          <p className="text-[#c5a059]/30 text-xs tracking-wider">
            dungeon-eyeswideshut.co.uk · Discretion Absolute
          </p>
        </div>
      </footer>
    </div>
  );
}

function TierCard({ name, range, description, active, tier }) {
  const tierStyles = {
    shadow: active ? 'border-[#c5a059]/40 bg-gradient-to-b from-[#0f0f11] to-[#0a0a0a]' : 'border-[#c5a059]/10',
    crimson: active ? 'border-[#8a0303]/60 bg-gradient-to-b from-[#1a0505] to-[#0a0a0a]' : 'border-[#c5a059]/10',
    obsidian: active ? 'border-[#c5a059]/60 bg-gradient-to-b from-[#1a1508] to-[#0a0a0a] shadow-[0_0_30px_rgba(197,160,89,0.1)]' : 'border-[#c5a059]/10',
  };

  return (
    <div className={`obsidian-card rounded-lg p-4 transition-all duration-300 ${tierStyles[tier]} ${active ? 'scale-[1.02]' : 'opacity-50'}`}>
      {active && (
        <div className="text-[10px] text-[#b30000] font-semibold tracking-wider uppercase mb-1">Current</div>
      )}
      <h3 className="font-cinzel text-sm tracking-wider mb-1" style={{ color: '#e5c07b' }}>
        {name}
      </h3>
      <p className="text-[#c5a059]/50 text-[10px] mb-2">{range}</p>
      <p className="text-[#c5a059]/70 text-xs leading-tight">{description}</p>
    </div>
  );
}
